export type AuthSessionEvent =
  | { type: "ended"; reason: "expired" | "logout" }
  | { type: "authenticated" }
  | { type: "reauth-required" };

type AuthSessionListener = (event: AuthSessionEvent, remote: boolean) => void;

const channelName = "truth-seeker-auth-session";
const listeners = new Set<AuthSessionListener>();
let channel: BroadcastChannel | null = null;
let authEpoch = 0;

function advanceAuthEpoch() {
  authEpoch += 1;
}

function emit(event: AuthSessionEvent, remote: boolean) {
  for (const listener of listeners) listener(event, remote);
}

function authChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(channelName);
    channel.onmessage = (message) => {
      const event = message.data as Partial<AuthSessionEvent>;
      if (event.type === "authenticated") {
        advanceAuthEpoch();
        emit({ type: "authenticated" }, true);
      } else if (event.type === "ended" && (event.reason === "expired" || event.reason === "logout")) {
        advanceAuthEpoch();
        emit({ type: "ended", reason: event.reason }, true);
      } else if (event.type === "reauth-required") {
        emit({ type: "reauth-required" }, true);
      }
    };
  }
  return channel;
}

export function subscribeAuthSession(listener: AuthSessionListener) {
  listeners.add(listener);
  authChannel();
  return () => {
    listeners.delete(listener);
  };
}

export function currentAuthEpoch() {
  return authEpoch;
}

/** Recheck a cookie/account mismatch without treating the new login as expired. */
export function requestAuthRevalidation() {
  advanceAuthEpoch();
  emit({ type: "authenticated" }, false);
}

export function publishAuthSessionEnded(reason: "expired" | "logout", requestAuthEpoch?: number) {
  if (requestAuthEpoch !== undefined && requestAuthEpoch !== authEpoch) return false;
  advanceAuthEpoch();
  const event: AuthSessionEvent = { type: "ended", reason };
  emit(event, false);
  authChannel()?.postMessage(event);
  return true;
}

export function publishAuthSessionReauthRequired(requestAuthEpoch?: number) {
  if (requestAuthEpoch !== undefined && requestAuthEpoch !== authEpoch) return false;
  const event: AuthSessionEvent = { type: "reauth-required" };
  emit(event, false);
  authChannel()?.postMessage(event);
  return true;
}

export function broadcastAuthenticatedSession() {
  advanceAuthEpoch();
  authChannel()?.postMessage({ type: "authenticated" } satisfies AuthSessionEvent);
}
